import { TFunction } from 'react-i18next';
import { format } from 'date-fns/format';
import {
  MAX_VARIABLE_NAME_LENGTH,
  MIN_VARIABLE_NAME_LENGTH,
  SMART_CONNECTOR_TYPES,
  STEP_DATA_BY_STEP,
  VARIABLE_TYPES,
} from '../constants';
import {
  CSVFlowLoad,
  FlowVariableType,
  GenericOption,
  SmartConnector,
  SmartConnectorBase,
  SmartConnectorDTO,
  SmartConnectorFlowLoad,
  SmartConnectorStep,
  SmartConnectorVariable,
} from '../types';
import { UTF_8_CSV_BOM } from 'components/DownloadButton/useCSVString';
import { getSmartConnectorTypeNames } from '../helpers';

export const getSmartConnectorTypeOptions = (t: TFunction) => {
  const typeNames = getSmartConnectorTypeNames(t);
  return [
    {
      value: SMART_CONNECTOR_TYPES.spreadsheet,
      label: typeNames.spreadsheet,
    },
    {
      value: SMART_CONNECTOR_TYPES.webhook,
      label: typeNames.webhook,
    },
    {
      value: SMART_CONNECTOR_TYPES.schedule,
      label: typeNames.schedule,
    },
    {
      value: SMART_CONNECTOR_TYPES.bulkaction,
      label: typeNames.bulkaction,
    },
    {
      value: SMART_CONNECTOR_TYPES.activity,
      label: typeNames.activity,
    },
  ];
};

export const getScriptResultMessage = (
  executionResult: Record<string, any> | null,
  t: TFunction
): string => {
  if (!executionResult) {
    return '';
  }
  const firstLine = t('Test ID {{executionId}} ran at {{timestamp}}', {
    executionId: executionResult.id?.split('-')[0] || '',
    timestamp: format(executionResult.completed_at, 'yyyy-MM-dd hh:mm:ss'),
  });
  const secondLine =
    executionResult.meta.error ||
    t(
      'Script ran successfully and generated: {{num_output_tables}} tables | {{num_columns}} columns | {{num_rows}} rows',
      executionResult.meta.stats
    );
  const thirdLine = executionResult.meta.error_details || '';

  return [firstLine, secondLine, thirdLine].filter(Boolean).join('\n');
};

export const toHtml = (value: string = '') => {
  return {
    type: 'doc',
    content: value
      .split('\n')
      .map((text) =>
        text
          ? { type: 'paragraph', content: [{ type: 'text', text }] }
          : { type: 'paragraph' }
      ),
  };
};

export const getStepData = (
  data: SmartConnector | SmartConnectorBase,
  step: SmartConnectorStep
) => {
  const stepProps = STEP_DATA_BY_STEP[step];

  return stepProps.reduce<Partial<SmartConnector>>((acc, prop) => {
    return {
      ...acc,
      [prop]: data[prop],
    };
  }, {});
};

export const getSmartConnectorDTO = (data: Partial<SmartConnector>) => {
  const {
    last_draft_script,
    live_script,
    source_file,
    execution_variables,
    kizen_data_seeds,
    headers,
    api_name,
    bulkaction_fields,
    activity_fields,
    activity_object,
    returnable_sources,
    ...rest
  } = data;

  const payload: SmartConnectorDTO = rest;

  if (source_file) {
    payload.source_file_id = source_file.id;
  }

  if (execution_variables) {
    payload.execution_variables = execution_variables
      .filter(({ scope }) => headers?.[scope]) // filter out variables that have invalid scope
      .map((variable, i) => ({
        ...variable,
        display_order: i,
      }));
  }

  if (kizen_data_seeds) {
    payload.kizen_data_seeds = kizen_data_seeds.map(
      ({ id, custom_object, fields, group }) => {
        return {
          id,
          custom_object_id: custom_object.id,
          fields_ids: fields.map(({ id }) => id),
          group_id: group?.id || null,
        };
      }
    );
  }

  if (payload.connector_type === SMART_CONNECTOR_TYPES.bulkaction) {
    payload.bulkaction_fields = (bulkaction_fields || []).map(({ id }) => id);
  }

  if (payload.connector_type === SMART_CONNECTOR_TYPES.activity) {
    payload.activity_fields = (activity_fields || []).map(({ id }) => id);
    if (activity_object?.id) {
      payload.activity_object = activity_object.id || null;
    }
  }

  return payload;
};

export const getUniqueName = (value: string, names: string[]) => {
  let nextValue = (value as string).trim();

  const uniqueNames = new Set(names);

  const copyRegEx = new RegExp(`^.+_\\d+$`);
  const copyNumberRegEx = new RegExp(`_\\d+$`);

  if (uniqueNames.has(nextValue)) {
    let i = 1;
    do {
      nextValue = nextValue.match(copyRegEx)
        ? nextValue.replace(copyNumberRegEx, `_${i}`)
        : `${nextValue}_${i}`;
      i++;
    } while (uniqueNames.has(nextValue));
  }

  return nextValue;
};

export const getSmartConnectorVariables = ({
  execution_variables,
  flow,
}: Pick<
  SmartConnector,
  'execution_variables' | 'flow'
>): SmartConnectorVariable[] => {
  const executionVariables = execution_variables || [];
  const additionalVariables = flow.additional_variables || [];

  const loadsVariables = (flow.loads || []).reduce<SmartConnectorVariable[]>(
    (acc, load) => {
      if (isCSVLoadStep(load)) {
        if (load.execution_variable) {
          acc.push(load.execution_variable);
        }
      } else if (load.return_scopes?.length) {
        const variables = load.return_scopes.reduce<SmartConnectorVariable[]>(
          (acc, scope) => {
            if (scope.execution_variables?.length) {
              acc.push(...scope.execution_variables);
            }
            return acc;
          },
          []
        );
        acc.push(...variables);
      }
      return acc;
    },
    []
  );

  return [...executionVariables, ...additionalVariables, ...loadsVariables];
};

const groupOrder = {
  [VARIABLE_TYPES.related_object_upload]: 0,
  [VARIABLE_TYPES.data_source]: 1,
  [VARIABLE_TYPES.additional]: 2,
};

export const getVariableTypeLabel = (type: FlowVariableType, t: TFunction) => {
  switch (type) {
    case 'additional':
      return t('Additional Variables');
    case 'data_source':
      return t('Data Source Variables');
    case 'related_object_upload':
      return t('Object Upload Variables');
  }
};

export const getVariablesGroupedOptions = (
  variables: SmartConnectorVariable[],
  t: TFunction
) => {
  return variables
    .reduce(
      (acc, variable) => {
        const index = variable.type ? groupOrder[variable.type] : -1;
        if (acc[index]) {
          acc[index].options.push({
            value: variable.id,
            label: variable.name,
          });
        }

        return acc;
      },
      [
        {
          label: getVariableTypeLabel('related_object_upload', t),
          options: [] as GenericOption[],
        },
        {
          label: getVariableTypeLabel('data_source', t),
          options: [] as GenericOption[],
        },
        {
          label: getVariableTypeLabel('additional', t),
          options: [] as GenericOption[],
        },
      ]
    )
    .filter(({ options }) => options.length > 0);
};

export const getVariableNamesByScope = (
  smartConnector: Pick<
    SmartConnector,
    'flow' | 'headers' | 'execution_variables'
  >
) =>
  Object.keys(smartConnector.headers || {}).reduce(
    (acc, headerScope) => {
      acc[headerScope] = getSmartConnectorVariables({
        execution_variables: (smartConnector.execution_variables || []).filter(
          ({ scope }) => scope === headerScope
        ),
        flow: {
          ...smartConnector.flow,
          loads: smartConnector.flow.loads.filter(
            (load) => isCSVLoadStep(load) && load.scope === headerScope
          ),
        },
      }).map(({ name }) => name);

      return acc;
    },
    {} as Record<string, string[]>
  );

export const generateCSV = (data: string[][]) =>
  `${UTF_8_CSV_BOM}${data.map((row) => row.join(',')).join('\n')}`;

export const getVariableNameError = (value: string, t: TFunction) => {
  let nameError = '';
  if (!value) {
    nameError = t('Variable name may not be blank.');
  } else if (value.length > MAX_VARIABLE_NAME_LENGTH) {
    nameError = t('Maximum name size is {{maxLength}} characters.', {
      maxLength: MAX_VARIABLE_NAME_LENGTH,
    });
  } else if (value.length < MIN_VARIABLE_NAME_LENGTH) {
    nameError = t('Minimum name size is {{minLength}} characters.', {
      minLength: MIN_VARIABLE_NAME_LENGTH,
    });
  } else if (!/^[a-z_][a-z0-9_]*$/.test(value)) {
    nameError = t(
      'Must start with a letter or underscore and contain only lowercase letters, numbers, or underscores.'
    );
  }
  return nameError;
};

export const isCSVLoadStep = (
  step: SmartConnectorFlowLoad
): step is CSVFlowLoad => {
  return step.type === 'csv_load';
};
